import { useEffect, useRef, useState } from "react";
import {
  EMPTY_STAGE_REQUIRED_FIELDS,
  type CustomFieldsSchema,
  type OpportunityStage,
  type OpportunityStandardRequiredField,
} from "@forgepoint/shared";
import { useOpportunityStagesStore } from "./opportunityStagesStore";
import {
  getStageRequiredFields,
  type StageRequiredField,
  type StageRequirementValues,
} from "./stageRequirements";

const FILLED_FLASH_MS = 1200;
const MISSING_FIELD_CLASS_NAME =
  "[&_input]:border-destructive/55 [&_[role=combobox]]:border-destructive/55";
const FILLED_FIELD_CLASS_NAME =
  "[&_input]:border-success/60 [&_[role=combobox]]:border-success/60";

/** The selected stage's required fields and how each field control should be marked. */
export interface StageRequirements {
  stage: OpportunityStage | undefined;
  fields: StageRequiredField[];
  missingFields: StageRequiredField[];
  customRequiredNames: string[];
  isStandardRequired: (field: OpportunityStandardRequiredField) => boolean;
  getStandardFieldClassName: (
    field: OpportunityStandardRequiredField,
  ) => string | undefined;
  getCustomFieldClassName: (name: string) => string | undefined;
}

/**
 * Tracks which of the selected stage's required fields are filled on a form.
 * A blank required field is outlined in destructive; once filled it briefly
 * turns success before returning to normal.
 * @param stageId - The stage selected on the form
 * @param values - The form's current values
 * @param customFieldsSchema - The org's opportunity custom fields schema, if any
 * @returns The stage, its required fields, and field marker helpers
 */
export function useStageRequirements(
  stageId: string | null,
  values: StageRequirementValues,
  customFieldsSchema: CustomFieldsSchema | null | undefined,
): StageRequirements {
  const stage = useOpportunityStagesStore((s) => s.stages.get(stageId ?? ""));
  const fields = getStageRequiredFields(
    stage?.requiredFields ?? EMPTY_STAGE_REQUIRED_FIELDS,
    values,
    customFieldsSchema,
  );
  const missingFields = fields.filter((field) => !field.isFilled);
  const missingKeys = missingFields.map(getFieldKey);
  const requiredKeys = fields.map(getFieldKey);

  const previousMissingRef = useRef<{
    stageId: string | null;
    keys: string[];
  } | null>(null);
  const [justFilledKeys, setJustFilledKeys] = useState<string[]>([]);

  useEffect(() => {
    const previous = previousMissingRef.current;
    previousMissingRef.current = { stageId, keys: missingKeys };
    if (!previous || previous.stageId !== stageId) {
      return;
    }
    const filledNow = previous.keys.filter((key) => !missingKeys.includes(key));
    if (filledNow.length > 0) {
      setJustFilledKeys(filledNow);
    }
  }, [stageId, missingKeys]);

  useEffect(() => {
    if (justFilledKeys.length === 0) {
      return;
    }
    const timeout = setTimeout(() => setJustFilledKeys([]), FILLED_FLASH_MS);
    return () => clearTimeout(timeout);
  }, [justFilledKeys]);

  const getClassName = (key: string): string | undefined => {
    if (missingKeys.includes(key)) {
      return MISSING_FIELD_CLASS_NAME;
    }
    if (justFilledKeys.includes(key)) {
      return FILLED_FIELD_CLASS_NAME;
    }
    return undefined;
  };

  return {
    stage,
    fields,
    missingFields,
    customRequiredNames: fields
      .filter((field) => field.kind === "custom")
      .map((field) => field.label),
    isStandardRequired: (field) => requiredKeys.includes(getStandardKey(field)),
    getStandardFieldClassName: (field) => getClassName(getStandardKey(field)),
    getCustomFieldClassName: (name) => getClassName(getCustomKey(name)),
  };
}

/**
 * Builds a key unique across standard and custom required fields.
 * @param field - The required field
 * @returns The field's key
 */
function getFieldKey(field: StageRequiredField): string {
  if (field.kind === "standard") {
    return getStandardKey(field.field);
  }
  return getCustomKey(field.name);
}

/**
 * Builds the key of a standard field.
 * @param field - The standard field
 * @returns The field's key
 */
function getStandardKey(field: OpportunityStandardRequiredField): string {
  return `standard:${field}`;
}

/**
 * Builds the key of a custom field.
 * @param name - The custom field name
 * @returns The field's key
 */
function getCustomKey(name: string): string {
  return `custom:${name}`;
}
